import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../services/supabase';
import { useAuth } from './AuthContext';
import {
  getConversations as fetchConversations,
  getUnreadCounts,
  markConversationRead as markReadService,
} from '../services/chatService';
import { createNotification } from '../services/notificationService';

const ChatContext = createContext({});

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};

export const ChatProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [totalUnread, setTotalUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const channelRef = useRef(null);
  // Mirror of `conversations` so the realtime handlers can read the current
  // list without re-subscribing on every change, and without doing side
  // effects inside a setState updater (React re-invokes those in StrictMode).
  const conversationsRef = useRef([]);

  useEffect(() => {
    conversationsRef.current = conversations;
  }, [conversations]);

  // Load conversations
  const loadConversations = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      const { data, error } = await fetchConversations();
      if (!error && data) {
        setConversations(data);
      }
      const { data: counts, total } = await getUnreadCounts();
      setUnreadCounts(counts || {});
      setTotalUnread(total || 0);
    } catch (err) {
      console.error('Failed to load conversations:', err);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Subscribe to realtime updates
  useEffect(() => {
    if (!isAuthenticated || !user?.id) return;

    loadConversations();

    // Listen for new messages across all conversations
    const channel = supabase
      .channel('chat-global-updates')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_messages',
        },
        async (payload) => {
          const newMsg = payload.new;

          // Update conversation list with new message
          setConversations(prev => {
            const idx = prev.findIndex(c => c.id === newMsg.conversation_id);
            if (idx === -1) {
              // New conversation we don't have yet - refresh
              loadConversations();
              return prev;
            }
            const updated = [...prev];
            const conv = updated[idx];
            updated[idx] = {
              ...conv,
              last_message_at: newMsg.created_at,
              lastMessage: {
                id: newMsg.id,
                content: newMsg.content,
                message_type: newMsg.message_type,
                created_at: newMsg.created_at,
                sender: null, // Will be enriched on next full load
              },
            };

            // Increment unread if not from current user and not active conversation
            if (newMsg.sender_id !== user.id && newMsg.conversation_id !== activeConversationId) {
              updated[idx].unreadCount = (updated[idx].unreadCount || 0) + 1;
              setUnreadCounts(counts => ({
                ...counts,
                [newMsg.conversation_id]: (counts[newMsg.conversation_id] || 0) + 1,
              }));
              setTotalUnread(t => t + 1);

              // Create notification if conversation is not muted
              if (!conv.isMuted) {
                const convName = conv.type === 'direct'
                  ? (conv.dmParticipant?.full_name || 'Chat directo')
                  : (conv.name || 'Conversación');
                const preview = newMsg.message_type === 'image'
                  ? 'Imagen adjunta'
                  : (newMsg.content?.substring(0, 80) || 'Nuevo mensaje');

                createNotification({
                  user_id: user.id,
                  notification_type: 'info',
                  title: `Nuevo mensaje en ${convName}`,
                  message: preview,
                  action_url: `/mensajes/${conv.id}`,
                  related_type: 'chat',
                  related_id: conv.id,
                }).catch(() => {}); // Fire-and-forget
              }
            }

            // Re-sort by last message time
            updated.sort((a, b) => {
              const timeA = new Date(a.last_message_at || a.created_at || 0);
              const timeB = new Date(b.last_message_at || b.created_at || 0);
              return timeB - timeA;
            });

            return updated;
          });
        }
      )
      // Edited / soft-deleted messages (#447 follow-up). This context runs its
      // own subscription, parallel to chatService's subscribeToConversations,
      // so it needed the same UPDATE listener: without it an edit or a
      // soft-delete (`UPDATE chat_messages SET is_deleted = true`) by another
      // participant left this context's `conversations` / `unreadCounts` /
      // `totalUnread` stale until a reload or the next loadConversations().
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'chat_messages',
        },
        (payload) => {
          const updatedMsg = payload.new;
          const conv = conversationsRef.current.find(c => c.id === updatedMsg.conversation_id);
          // Not a conversation we're showing — nothing in this state to fix.
          if (!conv) return;

          if (updatedMsg.is_deleted) {
            // Soft delete: neither value can be recomputed from this payload.
            // getConversations() picks the newest message WHERE is_deleted =
            // false, so the sidebar preview has to fall back to the previous
            // message (which we don't have here), and get_user_unread_counts()
            // counts the same way server-side, so the badge can drop. Re-sync
            // from the server instead of guessing — deletes are rare enough
            // that the extra round-trip is cheaper than drifting counters.
            loadConversations();
            return;
          }

          // Plain edit: ordering and unread counts don't change, so this only
          // matters when the edited message is the one the sidebar previews.
          if (conv.lastMessage?.id !== updatedMsg.id) return;
          setConversations(prev =>
            prev.map(c => (
              c.id === conv.id
                ? {
                    ...c,
                    lastMessage: {
                      ...c.lastMessage,
                      content: updatedMsg.content,
                      message_type: updatedMsg.message_type,
                    },
                  }
                : c
            ))
          );
        }
      )
      .subscribe();

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }
    };
  }, [isAuthenticated, user?.id, loadConversations, activeConversationId]);

  // Set active conversation and mark as read
  const setActiveConversation = useCallback(async (id) => {
    setActiveConversationId(id);
    if (id) {
      await markReadService(id);
      // Clear unread for this conversation
      setConversations(prev =>
        prev.map(c => c.id === id ? { ...c, unreadCount: 0 } : c)
      );
      setUnreadCounts(prev => {
        const updated = { ...prev };
        const prevCount = updated[id] || 0;
        delete updated[id];
        setTotalUnread(t => Math.max(0, t - prevCount));
        return updated;
      });
    }
  }, []);

  // Mark a specific conversation as read
  const markAsRead = useCallback(async (conversationId) => {
    const { success } = await markReadService(conversationId);
    if (success) {
      setConversations(prev =>
        prev.map(c => c.id === conversationId ? { ...c, unreadCount: 0 } : c)
      );
      setUnreadCounts(prev => {
        const updated = { ...prev };
        const prevCount = updated[conversationId] || 0;
        delete updated[conversationId];
        setTotalUnread(t => Math.max(0, t - prevCount));
        return updated;
      });
    }
  }, []);

  const value = {
    conversations,
    activeConversationId,
    unreadCounts,
    totalUnread,
    loading,
    loadConversations,
    setActiveConversation,
    markAsRead,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

export default ChatContext;
